<script setup lang="ts">
interface Props {
  pattern: string
}

const props = defineProps<Props>()

// Refs
const canvasRef = ref<HTMLCanvasElement | null>(null)
const containerRef = ref<HTMLDivElement | null>(null)

// State
const cellSize = ref(0)
const gridOffsetX = ref(0)
const gridOffsetY = ref(0)
const visibleCols = ref(30)
const visibleStartCol = ref(0)
const isAnimating = ref(false)
const rafId = ref<number | null>(null)
const resizeObserver = ref<ResizeObserver | null>(null)

// Parsed grid data (computed once)
interface Cell {
  char: string
  row: number
  col: number
  borders: {top: boolean; right: boolean; bottom: boolean; left: boolean}
  filled: boolean
  visible: boolean
}

interface RowAnimation {
  progress: number
  startTime: number
  delay: number
}

interface CellHoverState {
  targetScale: number
  currentScale: number
  targetShadowOpacity: number
  currentShadowOpacity: number
  targetBgOpacity: number
  currentBgOpacity: number
  lastUpdateTime: number
}

const cells = ref<Cell[]>([])
const rowAnimations = ref<RowAnimation[]>([])
const cellHoverStates = new Map<string, CellHoverState>()
const animationStartTime = ref(0)
const gridRows = ref(0)
const gridCols = ref(0)

// Constants
const ANIMATION_DURATION = 500
const ROW_DELAY_FACTOR = 1 / 25
const HOVER_SCALE = 1.25
const ENTRY_OFFSET_Y = -16
const HOVER_TRANSITION_IN = 75 // ms - fast scale up
const HOVER_TRANSITION_OUT = 500 // ms - slow scale down

// Parse pattern into cell data structure
function parsePattern() {
  const rows = props.pattern.split('\n').filter(Boolean)
  gridRows.value = rows.length
  gridCols.value = Math.max(...rows.map(r => r.length))

  const parsed: Cell[] = []

  rows.forEach((row, rowIdx) => {
    row.split('').forEach((char, colIdx) => {
      const borders = calculateBorders(rows, rowIdx, colIdx)
      parsed.push({
        char,
        row: rowIdx,
        col: colIdx,
        borders,
        filled: char === '2',
        visible: char !== ' ' && char !== 'X',
      })
    })
  })

  cells.value = parsed

  // Initialize row animations
  rowAnimations.value = rows.map((_, idx) => ({
    progress: 0,
    startTime: 0,
    delay: idx * ROW_DELAY_FACTOR * 1000,
  }))
}

// Calculate which borders a cell should have (port from original)
function calculateBorders(rows: string[], row: number, col: number) {
  const borders = {top: false, right: false, bottom: false, left: false}

  const currentRow = rows[row]
  if (!currentRow) return borders

  const current = currentRow.charAt(col)
  const above = rows[row - 1]?.charAt(col) || ''
  const below = rows[row + 1]?.charAt(col) || ''
  const left = currentRow.charAt(col - 1) || ''
  const right = currentRow.charAt(col + 1) || ''

  // Default borders
  borders.left = true
  borders.top = true
  if (current !== ' ' && !below) borders.bottom = true

  // Remove borders for spaces
  if (current === ' ' && (left === ' ' || !left)) borders.left = false
  if (current === ' ' && (above === ' ' || !above)) borders.top = false

  // Edge borders - add if neighbor is empty or doesn't exist
  if (current !== ' ' && (!right || right === ' ')) borders.right = true
  if (current !== ' ' && (!left || left === ' ')) borders.left = true
  if (current !== ' ' && (!above || above === ' ')) borders.top = true
  if (current !== ' ' && (!below || below === ' ')) borders.bottom = true

  // Remove all borders for 'X'
  if (current === 'X') {
    borders.left = false
    borders.right = false
    borders.bottom = false
    borders.top = false
  }

  return borders
}

// Get visible columns based on width (clamping at discrete points)
function getVisibleCols(width: number): number {
  if (width >= 1000) return 30
  if (width >= 800) return 24
  if (width >= 600) return 20
  if (width >= 400) return 16
  return 12
}

// Get cell size based on visible columns
function getCellSize(width: number, cols: number): number {
  return width / cols
}

// Setup canvas with device pixel ratio
function setupCanvas() {
  const canvas = canvasRef.value
  const container = containerRef.value
  if (!canvas || !container) return

  const rect = container.getBoundingClientRect()
  const dpr = window.devicePixelRatio || 1

  canvas.width = rect.width * dpr
  canvas.height = rect.height * dpr
  canvas.style.width = `${rect.width}px`
  canvas.style.height = `${rect.height}px`

  const ctx = canvas.getContext('2d')
  if (!ctx) return

  ctx.scale(dpr, dpr)

  // Calculate visible columns based on container width
  visibleCols.value = getVisibleCols(rect.width)

  // Calculate starting column to center the visible portion
  visibleStartCol.value = Math.max(0, Math.floor((gridCols.value - visibleCols.value) / 2))

  // Calculate cell size and grid centering
  cellSize.value = getCellSize(rect.width, visibleCols.value)
  const gridWidth = visibleCols.value * cellSize.value
  const gridHeight = gridRows.value * cellSize.value
  gridOffsetX.value = (rect.width - gridWidth) / 2
  gridOffsetY.value = 0 // Start at top
}

// Cubic bezier easing [0.16, 1, 0.3, 1]
function easeOutCubic(t: number): number {
  // Simplified approximation of the bezier curve
  return 1 - Math.pow(1 - t, 3)
}

// Linear interpolation
function lerp(start: number, end: number, t: number): number {
  return start + (end - start) * t
}

// Calculate animation progress for a row
function calculateRowProgress(rowAnim: RowAnimation, timestamp: number): number {
  if (!animationStartTime.value) return 0

  const elapsed = timestamp - animationStartTime.value - rowAnim.delay
  if (elapsed < 0) return 0
  if (elapsed > ANIMATION_DURATION) return 1

  const t = elapsed / ANIMATION_DURATION
  return easeOutCubic(t)
}

// Update hover animations for all cells
function updateHoverAnimations(timestamp: number): boolean {
  let hasActiveAnimations = false

  for (const [key, state] of cellHoverStates.entries()) {
    const elapsed = timestamp - state.lastUpdateTime
    const isScalingUp = state.targetScale > state.currentScale
    const duration = isScalingUp ? HOVER_TRANSITION_IN : HOVER_TRANSITION_OUT

    if (state.currentScale !== state.targetScale) {
      const progress = Math.min(elapsed / duration, 1)
      const eased = easeOutCubic(progress)

      const startScale = isScalingUp ? 1.0 : HOVER_SCALE
      state.currentScale = lerp(startScale, state.targetScale, eased)

      if (progress >= 1) {
        state.currentScale = state.targetScale
      } else {
        hasActiveAnimations = true
      }
    }

    if (state.currentShadowOpacity !== state.targetShadowOpacity) {
      const progress = Math.min(elapsed / duration, 1)
      const eased = easeOutCubic(progress)

      const startOpacity = isScalingUp ? 0 : 1
      state.currentShadowOpacity = lerp(startOpacity, state.targetShadowOpacity, eased)

      if (progress >= 1) {
        state.currentShadowOpacity = state.targetShadowOpacity
      } else {
        hasActiveAnimations = true
      }
    }

    if (state.currentBgOpacity !== state.targetBgOpacity) {
      const progress = Math.min(elapsed / duration, 1)
      const eased = easeOutCubic(progress)

      const startOpacity = isScalingUp ? 0 : 1
      state.currentBgOpacity = lerp(startOpacity, state.targetBgOpacity, eased)

      if (progress >= 1) {
        state.currentBgOpacity = state.targetBgOpacity
      } else {
        hasActiveAnimations = true
      }
    }

    // Clean up cells that have completed their scale-down animation
    if (state.currentScale === 1.0 && state.targetScale === 1.0) {
      cellHoverStates.delete(key)
    }
  }

  return hasActiveAnimations
}

// Draw a single cell
function drawCell(
  ctx: CanvasRenderingContext2D,
  cell: Cell,
  options: {opacity: number; offsetY: number; scale: number; shadowOpacity: number; bgOpacity: number}
) {
  // Skip cells outside visible range
  if (cell.col < visibleStartCol.value || cell.col >= visibleStartCol.value + visibleCols.value) {
    return
  }

  ctx.save()

  // Adjust column position relative to visible start
  const visibleCol = cell.col - visibleStartCol.value
  const x = visibleCol * cellSize.value + gridOffsetX.value
  const y = cell.row * cellSize.value + gridOffsetY.value + options.offsetY
  const size = cellSize.value

  ctx.globalAlpha = options.opacity

  // Apply scale transform
  if (options.scale !== 1) {
    const centerX = x + size / 2
    const centerY = y + size / 2
    ctx.translate(centerX, centerY)
    ctx.scale(options.scale, options.scale)
    ctx.translate(-centerX, -centerY)
  }

  // Draw shadow with animated opacity
  if (options.shadowOpacity > 0) {
    ctx.shadowColor = `rgba(0, 0, 0, ${0.1 * options.shadowOpacity})`
    ctx.shadowBlur = 15 * options.shadowOpacity
    ctx.shadowOffsetX = 0
    ctx.shadowOffsetY = 4 * options.shadowOpacity
  }

  // Draw opaque white background when hovering (to hide borders underneath)
  if (options.bgOpacity > 0 && !cell.filled) {
    ctx.fillStyle = `rgba(255, 255, 255, ${options.bgOpacity})`
    ctx.fillRect(x, y, size, size)
  }

  // Fill background for filled cells or hover
  const actualBgOpacity = cell.filled ? 1.0 : options.bgOpacity
  if (actualBgOpacity > 0) {
    ctx.fillStyle = `rgba(243, 244, 246, ${actualBgOpacity})` // gray-100
    ctx.fillRect(x, y, size, size)
  }

  // Draw borders
  ctx.strokeStyle = 'rgba(229, 231, 235, 0.75)' // gray-200/75
  ctx.lineWidth = 1
  ctx.beginPath()

  if (cell.borders.top) {
    ctx.moveTo(x, y)
    ctx.lineTo(x + size, y)
  }
  if (cell.borders.right) {
    ctx.moveTo(x + size, y)
    ctx.lineTo(x + size, y + size)
  }
  if (cell.borders.bottom) {
    ctx.moveTo(x, y + size)
    ctx.lineTo(x + size, y + size)
  }
  if (cell.borders.left) {
    ctx.moveTo(x, y)
    ctx.lineTo(x, y + size)
  }

  ctx.stroke()
  ctx.restore()
}

// Main render loop
function render(timestamp: number) {
  const canvas = canvasRef.value
  if (!canvas) return

  const ctx = canvas.getContext('2d')
  if (!ctx) return

  // Clear canvas
  ctx.clearRect(0, 0, canvas.width, canvas.height)

  let animationComplete = true
  const hasActiveHoverAnimations = updateHoverAnimations(timestamp)

  // Collect cells to draw, separated by hover state
  const normalCells: Array<{
    cell: Cell
    opacity: number
    offsetY: number
    scale: number
    shadowOpacity: number
    bgOpacity: number
  }> = []
  const hoveredCells: Array<{
    cell: Cell
    opacity: number
    offsetY: number
    scale: number
    shadowOpacity: number
    bgOpacity: number
  }> = []

  for (const cell of cells.value) {
    if (!cell.visible) continue

    const rowAnim = rowAnimations.value[cell.row]
    if (!rowAnim) continue
    const progress = calculateRowProgress(rowAnim, timestamp)

    if (progress <= 0) {
      animationComplete = false
      continue
    }

    if (progress < 1) animationComplete = false

    const opacity = progress
    const offsetY = ENTRY_OFFSET_Y * (1 - progress)

    // Get hover state for this cell
    const cellKey = `${cell.row}-${cell.col}`
    const hoverState = cellHoverStates.get(cellKey)
    const scale = hoverState?.currentScale || 1.0
    const shadowOpacity = hoverState?.currentShadowOpacity || 0
    const bgOpacity = cell.filled ? 1.0 : hoverState?.currentBgOpacity || 0

    const cellData = {cell, opacity, offsetY, scale, shadowOpacity, bgOpacity}

    // Separate hovered cells (scale > 1) to draw them last (on top)
    if (scale > 1.0 || shadowOpacity > 0 || (bgOpacity > 0 && !cell.filled)) {
      hoveredCells.push(cellData)
    } else {
      normalCells.push(cellData)
    }
  }

  // Draw normal cells first
  for (const cellData of normalCells) {
    drawCell(ctx, cellData.cell, {
      opacity: cellData.opacity,
      offsetY: cellData.offsetY,
      scale: cellData.scale,
      shadowOpacity: cellData.shadowOpacity,
      bgOpacity: cellData.bgOpacity,
    })
  }

  // Draw hovered cells on top (so they cover borders underneath)
  for (const cellData of hoveredCells) {
    drawCell(ctx, cellData.cell, {
      opacity: cellData.opacity,
      offsetY: cellData.offsetY,
      scale: cellData.scale,
      shadowOpacity: cellData.shadowOpacity,
      bgOpacity: cellData.bgOpacity,
    })
  }

  // Continue animation if needed
  if (!animationComplete || hasActiveHoverAnimations) {
    rafId.value = requestAnimationFrame(render)
  } else {
    isAnimating.value = false
    rafId.value = null
  }
}

// Start animation
function startAnimation() {
  if (isAnimating.value) return

  isAnimating.value = true
  animationStartTime.value = performance.now()
  rafId.value = requestAnimationFrame(render)
}

// Mouse interaction handlers
function handleMouseMove(e: MouseEvent) {
  const canvas = canvasRef.value
  if (!canvas) return

  const rect = canvas.getBoundingClientRect()
  const x = e.clientX - rect.left
  const y = e.clientY - rect.top

  // Calculate visible column, then add offset to get actual column
  const visibleCol = Math.floor((x - gridOffsetX.value) / cellSize.value)
  const col = visibleCol + visibleStartCol.value
  const row = Math.floor((y - gridOffsetY.value) / cellSize.value)

  // Check if mouse is within visible column range
  if (visibleCol < 0 || visibleCol >= visibleCols.value) {
    handleMouseLeave()
    return
  }

  const cell = cells.value.find(c => c.row === row && c.col === col)
  const hoveredKey = cell?.visible ? `${row}-${col}` : null

  const timestamp = performance.now()

  // Update all cell hover states
  for (const [key, state] of cellHoverStates.entries()) {
    if (key !== hoveredKey && state.targetScale === HOVER_SCALE) {
      // Start scale down animation
      state.targetScale = 1.0
      state.targetShadowOpacity = 0
      state.targetBgOpacity = 0
      state.lastUpdateTime = timestamp
    }
  }

  // Set or update hovered cell
  if (hoveredKey) {
    let state = cellHoverStates.get(hoveredKey)
    if (!state) {
      state = {
        targetScale: HOVER_SCALE,
        currentScale: 1.0,
        targetShadowOpacity: 1.0,
        currentShadowOpacity: 0,
        targetBgOpacity: 1.0,
        currentBgOpacity: 0,
        lastUpdateTime: timestamp,
      }
      cellHoverStates.set(hoveredKey, state)
    } else if (state.targetScale !== HOVER_SCALE) {
      // Resume scale up animation
      state.targetScale = HOVER_SCALE
      state.targetShadowOpacity = 1.0
      state.targetBgOpacity = 1.0
      state.lastUpdateTime = timestamp
    }

    if (!isAnimating.value && !rafId.value) {
      rafId.value = requestAnimationFrame(render)
    }
  }
}

function handleMouseLeave() {
  const timestamp = performance.now()

  // Scale down all hovered cells
  for (const [key, state] of cellHoverStates.entries()) {
    if (state.targetScale === HOVER_SCALE) {
      state.targetScale = 1.0
      state.targetShadowOpacity = 0
      state.targetBgOpacity = 0
      state.lastUpdateTime = timestamp
    }
  }

  if (!isAnimating.value && !rafId.value && cellHoverStates.size > 0) {
    rafId.value = requestAnimationFrame(render)
  }
}

// Resize handler with debounce
let resizeTimeout: ReturnType<typeof setTimeout> | null = null
function handleResize() {
  if (resizeTimeout) clearTimeout(resizeTimeout)

  resizeTimeout = setTimeout(() => {
    setupCanvas()
    if (rafId.value === null) {
      rafId.value = requestAnimationFrame(render)
    }
  }, 100)
}

// Lifecycle
onMounted(() => {
  const canvas = canvasRef.value
  const container = containerRef.value

  if (!canvas || !container) return
  if (!canvas.getContext) return // Canvas not supported - fail silently

  parsePattern()
  setupCanvas()
  startAnimation()

  // Setup resize observer
  resizeObserver.value = new ResizeObserver(handleResize)
  resizeObserver.value.observe(container)
})

onUnmounted(() => {
  if (rafId.value) {
    cancelAnimationFrame(rafId.value)
  }
  if (resizeObserver.value) {
    resizeObserver.value.disconnect()
  }
  if (resizeTimeout) {
    clearTimeout(resizeTimeout)
  }
  cellHoverStates.clear()
})
</script>

<template>
  <div ref="containerRef" class="w-full" aria-hidden="true">
    <canvas ref="canvasRef" class="block" @mousemove="handleMouseMove" @mouseleave="handleMouseLeave" />
  </div>
</template>

<style scoped>
canvas {
  display: block;
}
</style>
